<template>
  <div style="height: 100%">
    <v-card style="height: 100%">
      <v-card-text>
        <v-row class="mt-3">
          <v-col cols="12" align="center">
            <h1>Labor Efficiency Ratio</h1>
            <br>
            <h2>YTD: {{ yearToDateLER }}</h2>
            <br>
            <h2>Last Month: {{ lastMonthLER }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <area-chart
              :data="lineChartData"
              :round="2"
              thousands=","
              loading="Loading..."
              :colors="chartColor"
            ></area-chart>
          </v-col>
        </v-row>
        <v-row v-if="showDetailsButton">
          <v-col cols="12" align="center">
            <v-btn
              text
              color="primary"
              :disabled="disableButtons"
              @click="showLERDetails"
            >
              Details
            </v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" align="end">
            <v-btn
              v-if="!showFetchingDataGIF"
              color="primary"
              outlined
              class="ml-2"
              @click="refreshTile"
              style="float: left;"
              >
                <v-icon>refresh</v-icon>
            </v-btn>
            <i
              v-if="showFetchingDataGIF"
              class="mr-2"
            >
              Fetching Data
            </i>
            <v-progress-circular
              v-if="showFetchingDataGIF"
              :size="30"
              :width="7"
              indeterminate
              color="primary"
            ></v-progress-circular>
            <i
              v-if="lastRefreshTime"
              class="ml-10 mr-5"
            >
              Last Update: {{ lastRefreshTime }}
            </i>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <DynamicModal
      :showModal="showLERDetailsModal"
      :persistent="false"
      @clickOutside="closeLERDetails"
      title="LER Details"
      max-width="1000px"
    >
      <v-container fluid slot="title-buttons" class="py-1">
        <v-row justify="end">
          <v-btn
            icon
            depressed
            color="primary"
            @click="closeLERDetails"
          >
            <v-icon color="white">close</v-icon>
          </v-btn>
        </v-row>
      </v-container>
      <v-container fluid slot="content">
        <LaborEfficiencyRatioDetails
          :laborEfficiencyRatioMonths="laborEfficiencyRatioMonths"
        ></LaborEfficiencyRatioDetails>
      </v-container>
      <v-container fluid slot="buttons" class="pb-0 mb-0">
        <v-row>
          <v-col cols="12">
            <v-btn
              block
              depressed
              outlined
              @click="closeLERDetails"
            >
              Close
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </DynamicModal>
  </div>
</template>

<script>
import { DynamicModal } from '@/components/dialogs'
import LaborEfficiencyRatioDetails from '@/components/dashboard/laborEfficiencyRatio/LaborEfficiencyRatioDetails'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { ADMIN } from '@/lib/constants/securityRoles'
import { mapActions } from 'vuex'
import { isJSONString } from '@/lib/miscellaneous'
import { getLocalTimeString } from '@/lib/display'

export default {
  components: {
    LaborEfficiencyRatioDetails,
    DynamicModal
  },
  data () {
    return {
      laborEfficiencyRatioData: null,
      lineChartData: [],
      lastMonthLER: 0,
      yearToDateLER: 0,
      firstPeriodLER: 0,
      showLERDetailsModal: false,
      laborEfficiencyRatioMonths: [],
      lastRefreshTime: null,
      showFetchingDataGIF: false,
      disableButtons: false
    }
  },
  watch: {
    laborEfficiencyRatioData () {
      if (this.laborEfficiencyRatioData) {
        this.lastMonthLER = this.laborEfficiencyRatioData.lastMonthLER ? parseFloat(this.laborEfficiencyRatioData.lastMonthLER).toFixed(2) : ''
        this.yearToDateLER = this.laborEfficiencyRatioData.yearToDateLER ? parseFloat(this.laborEfficiencyRatioData.yearToDateLER).toFixed(2) : ''
        this.firstPeriodLER = this.laborEfficiencyRatioData.items[0].ler
        this.laborEfficiencyRatioMonths = this.laborEfficiencyRatioData.items
        let chartData = {}
        for (const item of this.laborEfficiencyRatioData.items) {
          chartData[item.period] = item.ler
        }
        this.lineChartData = chartData
      }
    }
  },
  computed: {
    chartColor () {
      if (this.lastMonthLER >= this.firstPeriodLER) {
        return ['#80ff80']
      } else {
        return ['#ff6666']
      }
    },
    showDetailsButton () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN] })
    }
  },
  methods: {
    ...mapActions('dashboard', [
      'getLaborEfficiencyRatioData'
    ]),
    showLERDetails () {
      this.showLERDetailsModal = true
    },
    closeLERDetails () {
      this.showLERDetailsModal = false
    },
    refreshTile () {
      this.showFetchingDataGIF = true
      this.disableButtons = true
      this.getLaborEfficiencyRatioData().then((data) => {
        if (data) {
          let dateTimeNow = new Date()
          this.laborEfficiencyRatioData = data
          localStorage.setItem('dashboard:labor-efficiency-ratio', JSON.stringify(data))
          localStorage.setItem('dashboard:labor-efficiency-ratio-last-refresh-time', JSON.stringify(dateTimeNow.toUTCString()))
          this.lastRefreshTime = getLocalTimeString(dateTimeNow)
        }
        this.showFetchingDataGIF = false
        this.disableButtons = false
      })
    }
  },
  created () {
    if (localStorage.getItem('dashboard:labor-efficiency-ratio') && isJSONString(localStorage.getItem('dashboard:labor-efficiency-ratio'))) {
      this.laborEfficiencyRatioData = JSON.parse(localStorage.getItem('dashboard:labor-efficiency-ratio'))
    }
    if (localStorage.getItem('dashboard:labor-efficiency-ratio-last-refresh-time') && isJSONString(localStorage.getItem('dashboard:labor-efficiency-ratio-last-refresh-time'))) {
      let lastRefreshTime = new Date(JSON.parse(localStorage.getItem('dashboard:labor-efficiency-ratio-last-refresh-time')))
      this.lastRefreshTime = getLocalTimeString(lastRefreshTime)
    }
    this.refreshTile()
  }
}
</script>
